import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs';
import { ViewContentComponent } from '@sh/content/components/view-content/view-content.component';
import { IResourceDetail } from '@sh/resources/interfaces/i-resource-detail';
import { ResourcesService } from '@sh/resources/services/resources.service';
import { ShelfService } from '@sh/services/shelf/shelf.service';
import { RouterLink } from '@angular/router';
import { FeaturedResourcesComponent } from '../../components/featured-resources/featured-resources.component';
import { ExploreSubjectsComponent } from '../../components/explore-subjects/explore-subjects.component';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    RouterLink,
    FeaturedResourcesComponent,
    ExploreSubjectsComponent,
    ViewContentComponent,
  ],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
})
export class HomeComponent {
  private readonly resources = inject(ResourcesService);
  private readonly shelves = inject(ShelfService);
  private readonly destroyRef = inject(DestroyRef);
  demoResource: IResourceDetail | null = null;
  demoLoading = false;
  demoError = '';

  scrollToElement(id: string) {
    document.getElementById(id)?.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth',
      block: 'start',
    });
  }

  loadDemo() {
    if (this.demoLoading) return;
    this.demoLoading = true;
    this.demoError = '';
    this.shelves
      .getFeaturedShelf()
      .pipe(
        switchMap((shelf) => {
          if (!shelf.data?.featurableId) throw new Error('No featured shelf');
          return this.shelves.searchShelfItems({
            query: '',
            page: 1,
            pageSize: 20,
            shelfIds: [shelf.data.featurableId],
            shelfItemIds: [],
          });
        }),
        switchMap((response) => {
          const item = response.data.items.find(
            (item) => item.shelfItemType?.toLowerCase() === 'resource',
          );
          if (!item) throw new Error('No demo resource');
          return this.resources.getResource(item.contentId);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (response) => {
          this.demoResource = response.data;
          this.demoLoading = false;
          if (!response.data)
            this.demoError = 'The demo is unavailable. Please try again.';
        },
        error: () => {
          this.demoLoading = false;
          this.demoError = 'The demo could not be loaded. Please try again.';
        },
      });
  }
}
